import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function ExerciseFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workoutType, setWorkoutType] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const videoRef = useRef(null);
  const [editingPost, setEditingPost] = useState(null);

  const handleEditPost = (post) => {
    setSelectedWorkout(null);
    setEditingPost(post);
  };
  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
    setEditingPost(null);
  };
  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p._id !== postId));
    setSelectedWorkout(null);
  };

  const workoutTypes = [
    { id: "all", name: "Todos" },
    { id: "strength", name: "Força" },
    { id: "cardio", name: "Cardio" },
    { id: "hiit", name: "HIIT" },
    { id: "stretching", name: "Alongamento" },
    { id: "functional", name: "Funcional" },
    { id: "crossfit", name: "CrossFit" },
    { id: "pilates", name: "Pilates" },
  ];

  const levels = {
    beginner: "Iniciante",
    intermediate: "Intermediário",
    advanced: "Avançado",
  };

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/exercise?page=${pageNum}&limit=12`;
      if (workoutType !== "all") url += `&workoutType=${workoutType}`;

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching exercise:", error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); fetchPosts(1); }, [workoutType]);

  if (loading && posts.length === 0) {
    return <div className="exercise_feed_loading"><div className="exercise_loader"></div><span>Carregando treinos...</span></div>;
  }

  return (
    <div className="exercise_feed">
      {workoutTypes.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {workoutTypes.map((filter) => (
              <button
                key={filter.id}
                className={`filter_btn ${workoutType === filter.id ? "active" : ""}`}
                onClick={() => setWorkoutType(filter.id)}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="exercise_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M20.57 14.86L22 13.43 20.57 12 17 15.57 8.43 7 12 3.43 10.57 2 9.14 3.43 7.71 2 5.57 4.14 4.14 2.71 2.71 4.14l1.43 1.43L2 7.71l1.43 1.43L2 10.57 3.43 12 7 8.43 15.57 17 12 20.57 13.43 22l1.43-1.43L16.29 22l2.14-2.14 1.43 1.43 1.43-1.43-1.43-1.43L22 16.29z"/>
          </svg>
          <h3>Nenhum treino encontrado</h3>
          <p>Compartilhe seu treino!</p>
        </div>
      ) : (
        <div className="exercise_grid">
          {posts.map((post) => (
            <div key={post._id} className="exercise_card" onClick={() => setSelectedWorkout(post)}>
              <div className="exercise_card_thumbnail">
                {post.exerciseData?.video ? (
                  <video src={post.exerciseData.video} muted />
                ) : (
                  <img src={post.exerciseData?.thumbnail || post.images?.[0]?.url || "/images/default-exercise.png"} alt="" />
                )}
                <div className="exercise_play_overlay">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
                <span className="exercise_duration">{post.exerciseData?.duration || 0} min</span>
                <span className={`exercise_level ${post.exerciseData?.level || "beginner"}`}>
                  {levels[post.exerciseData?.level] || "Iniciante"}
                </span>
              </div>
              <div className="exercise_card_content">
                <span className="exercise_type">
                  {workoutTypes.find(w => w.id === post.exerciseData?.workoutType)?.name || "Treino"}
                </span>
                <h3>{post.exerciseData?.title || post.text}</h3>
                <div className="exercise_stats">
                  {post.exerciseData?.calories && (
                    <span>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                        <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67z"/>
                      </svg>
                      {post.exerciseData.calories} kcal
                    </span>
                  )}
                  {post.exerciseData?.sets?.length > 0 && (
                    <span>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                      </svg>
                      {post.exerciseData.sets.length} exercícios
                    </span>
                  )}
                </div>
                {post.exerciseData?.muscles?.length > 0 && (
                  <div className="exercise_muscles">
                    {post.exerciseData.muscles.slice(0, 3).map((m, i) => (
                      <span key={i}>{m}</span>
                    ))}
                  </div>
                )}
                <Link to={`/profile/${post.user.username}`} className="exercise_trainer" onClick={(e) => e.stopPropagation()}>
                  <img src={post.user.picture} alt="" />
                  <span>{post.user.first_name}</span>
                </Link>
                <div onClick={(e) => e.stopPropagation()}>
                  <FeedPostMenu
                    post={post}
                    onEdit={handleEditPost}
                    onPostDeleted={handlePostDeleted}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="exercise_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais treinos"}
          </button>
        </div>
      )}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}

      {selectedWorkout && (
        <div className="exercise_modal_overlay" onClick={() => setSelectedWorkout(null)}>
          <div className="exercise_modal" onClick={(e) => e.stopPropagation()}>
            <button className="exercise_modal_close" onClick={() => setSelectedWorkout(null)}>✕</button>

            <div className="exercise_modal_video">
              {selectedWorkout.exerciseData?.video ? (
                <video ref={videoRef} src={selectedWorkout.exerciseData.video} controls />
              ) : (
                <img src={selectedWorkout.exerciseData?.thumbnail || selectedWorkout.images?.[0]} alt="" />
              )}
            </div>

            <div className="exercise_modal_body">
              <div className="exercise_modal_meta">
                <span className="exercise_modal_type">
                  {workoutTypes.find(w => w.id === selectedWorkout.exerciseData?.workoutType)?.name}
                </span>
                <span className={`exercise_modal_level ${selectedWorkout.exerciseData?.level || "beginner"}`}>
                  {levels[selectedWorkout.exerciseData?.level] || "Iniciante"}
                </span>
              </div>
              <h2>{selectedWorkout.exerciseData?.title}</h2>

              <div className="exercise_modal_stats">
                <div className="exercise_stat_item">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                  </svg>
                  <div>
                    <span className="exercise_stat_value">{selectedWorkout.exerciseData?.duration || 0}</span>
                    <span className="exercise_stat_label">minutos</span>
                  </div>
                </div>
                {selectedWorkout.exerciseData?.calories && (
                  <div className="exercise_stat_item">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                      <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67z"/>
                    </svg>
                    <div>
                      <span className="exercise_stat_value">{selectedWorkout.exerciseData.calories}</span>
                      <span className="exercise_stat_label">kcal</span>
                    </div>
                  </div>
                )}
                <div className="exercise_stat_item">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                  </svg>
                  <div>
                    <span className="exercise_stat_value">{selectedWorkout.exerciseData?.sets?.length || 0}</span>
                    <span className="exercise_stat_label">exercícios</span>
                  </div>
                </div>
              </div>

              {selectedWorkout.exerciseData?.muscles?.length > 0 && (
                <div className="exercise_modal_section">
                  <h4>Músculos trabalhados</h4>
                  <div className="exercise_muscles_list">
                    {selectedWorkout.exerciseData.muscles.map((m, i) => (
                      <span key={i}>{m}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedWorkout.exerciseData?.sets?.length > 0 && (
                <div className="exercise_modal_section">
                  <h4>Exercícios</h4>
                  <div className="exercise_sets_list">
                    {selectedWorkout.exerciseData.sets.map((set, i) => (
                      <div key={i} className="exercise_set_item">
                        <span className="exercise_set_number">{i + 1}</span>
                        <div className="exercise_set_info">
                          <span className="exercise_set_name">{set.name}</span>
                          <span className="exercise_set_reps">{set.reps} reps × {set.sets} séries</span>
                        </div>
                        {set.rest && <span className="exercise_set_rest">{set.rest}s descanso</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedWorkout.exerciseData?.equipment?.length > 0 && (
                <div className="exercise_modal_section">
                  <h4>Equipamentos</h4>
                  <div className="exercise_equipment_list">
                    {selectedWorkout.exerciseData.equipment.map((e, i) => (
                      <span key={i}>{e}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedWorkout.exerciseData?.description && (
                <div className="exercise_modal_section">
                  <h4>Descrição</h4>
                  <p>{selectedWorkout.exerciseData.description}</p>
                </div>
              )}

              <Link to={`/profile/${selectedWorkout.user.username}`} className="exercise_modal_trainer">
                <img src={selectedWorkout.user.picture} alt="" />
                <div>
                  <span className="exercise_trainer_label">Personal</span>
                  <span className="exercise_trainer_name">{selectedWorkout.user.first_name} {selectedWorkout.user.last_name}</span>
                </div>
              </Link>

              <button className="exercise_start_btn">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                Iniciar treino
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
